import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const EMPTY = { name: "", domain: "", port: 5061, transport: "WSS", site_id: "", status: "active" };

export function ServersTab() {
  const [servers, setServers] = useState([]);
  const [sites, setSites] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => {
    api.get("/admin/servers").then((res) => setServers(res.data)).catch(() => {});
    api.get("/admin/sites").then((res) => setSites(res.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const siteName = (id) => sites.find((s) => s.id === id)?.name || "—";

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ name: s.name, domain: s.domain, port: s.port, transport: s.transport, site_id: s.site_id || "", status: s.status });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, port: Number(form.port), site_id: form.site_id || null };
    try {
      if (editing) await api.put(`/admin/servers/${editing.id}`, payload);
      else await api.post("/admin/servers", payload);
      toast.success(editing ? "Server updated" : "Server created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (s) => {
    try {
      await api.delete(`/admin/servers/${s.id}`);
      toast.success("Server deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="servers-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-100">SIP Servers</h3>
        <Button data-testid="create-server-btn" onClick={openCreate} size="sm"
          className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> New Server
        </Button>
      </div>

      <div className="ops-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="servers-table">
            <thead>
              <tr className="text-left ops-caption border-b border-[#1E2638] bg-[#0A0D14]">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Domain</th>
                <th className="px-4 py-3">Port</th>
                <th className="px-4 py-3">Transport</th>
                <th className="px-4 py-3">Site</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {servers.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="servers-empty">No SIP servers yet.</td></tr>
              )}
              {servers.map((s) => (
                <tr key={s.id} className="border-b border-[#1E2638]/50 last:border-0 hover:bg-[#0A0D14]/60" data-testid={`server-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-100">{s.name}</td>
                  <td className="px-4 py-3 font-mono text-xs text-emerald-400">{s.domain}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{s.port}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-400">{s.transport}</td>
                  <td className="px-4 py-3 text-slate-300">{siteName(s.site_id)}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-mono border ${
                      s.status === "active"
                        ? "text-emerald-400 bg-emerald-500/10 border-emerald-500/30"
                        : "text-slate-400 bg-slate-500/10 border-slate-500/30"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${s.status === "active" ? "bg-emerald-400 pulse-dot" : "bg-slate-500"}`} />
                      {s.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button data-testid={`edit-server-${s.id}`} variant="ghost" size="icon" onClick={() => openEdit(s)}
                      className="h-8 w-8 text-slate-400 hover:text-slate-100"><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button data-testid={`delete-server-${s.id}`} variant="ghost" size="icon" onClick={() => remove(s)}
                      className="h-8 w-8 text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F131C] border-[#1E2638] text-slate-100" data-testid="server-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit SIP Server" : "New SIP Server"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">Name</Label>
                <Input data-testid="server-name-input" value={form.name} required
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-[#0A0D14] border-[#1E2638]" placeholder="scharpz-live-01" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Domain / IP</Label>
                <Input data-testid="server-domain-input" value={form.domain} required
                  onChange={(e) => setForm({ ...form, domain: e.target.value })}
                  className="bg-[#0A0D14] border-[#1E2638]" placeholder="sip.scharpz.uk" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Port</Label>
                <Input data-testid="server-port-input" type="number" value={form.port} required
                  onChange={(e) => setForm({ ...form, port: e.target.value })}
                  className="bg-[#0A0D14] border-[#1E2638]" />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Transport</Label>
                <Select value={form.transport} onValueChange={(v) => setForm({ ...form, transport: v })}>
                  <SelectTrigger data-testid="server-transport-select" className="bg-[#0A0D14] border-[#1E2638]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                    {["WSS", "WS", "TLS", "TCP", "UDP"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Site</Label>
                <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="server-site-select" className="bg-[#0A0D14] border-[#1E2638]">
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                    <SelectItem value="none">Unassigned</SelectItem>
                    {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="server-status-select" className="bg-[#0A0D14] border-[#1E2638]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" data-testid="server-submit-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
              {editing ? "Save Changes" : "Create Server"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
